import styled, { css, createGlobalStyle } from 'styled-components'
import { cardisTheme } from '../../theme/cardisTheme'

// Global print styles to remove page margins for full-bleed cover
// IMPORTANT: @page rules must be at top level, NOT inside @media print
export const CardisCoverPrintStyles = createGlobalStyle`
  @page :first {
    margin: 0 !important;
    size: A4 !important;
  }
`

const printStyles = css`
  @media print {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CoverContainer = styled.div`
  width: 100%;
  height: 311mm;
  margin: 0;
  position: relative;
  overflow: hidden;
  font-family: ${cardisTheme.fonts.body};
  box-sizing: border-box;
  background-color: ${cardisTheme.colors.backgroundGray};
  margin-left: -3.3rem;
  margin-right: -3.3rem;
  width: calc(100% + 6.6rem);

  @media print {
    display: block !important;
    position: relative !important;
    overflow: hidden !important;
    page-break-after: always !important;
    page-break-inside: avoid !important;
    background-color: ${cardisTheme.colors.backgroundGray} !important;
    ${printStyles}
  }
`

export const BackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;

  @media print {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    ${printStyles}
  }
`

export const OverlayImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  pointer-events: none;

  @media print {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    ${printStyles}
  }
`

export const ContentOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media print {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }
`

export const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px 38px;
  margin-top: 32px;
  margin-bottom: 48px;

  @media print {
    display: flex !important;
    justify-content: flex-end !important;
  }
`

export const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

export const Logo = styled.img`
  max-width: 128px;
  height: auto;
  object-fit: contain;

  @media print {
    max-width: 128px !important;
    height: auto !important;
    min-height: auto !important;
    ${printStyles}
  }
`

export const Tagline = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-style: italic;
  color: ${cardisTheme.colors.primary};
  margin: 16px 16px 0px 0px;
  text-align: right;

  @media print {
    font-size: 10px !important;
    margin: 16px 16px 0px 0px !important;
    ${printStyles}
  }
`

export const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 32px 51px;
  min-height: 0;

  @media print {
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
  }
`

export const TitleSection = styled.div`
  margin-bottom: 131px;
`

export const Title = styled.h1`
  font-family: ${cardisTheme.fonts.body};
  font-size: 36px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.1;

  @media print {
    font-size: 36px !important;
    font-weight: 600 !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    line-height: 1.1 !important;
    ${printStyles}
  }
`

export const ReportDate = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 5px 0 0 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media print {
    font-size: 12px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 5px 0 0 0 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    ${printStyles}
  }
`

export const PropertySection = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media print {
    flex: 1 !important;
    display: flex !important;
  }
`

export const PropertyInfo = styled.div`
  flex: 1;
  max-width: 50%;
`

export const PropertyType = styled.h2`
  font-family: ${cardisTheme.fonts.body};
  font-size: 26px;
  font-weight: 500;
  font-style: italic;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 6px 0;

  @media print {
    font-size: 26px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 0 6px 0 !important;
    font-style: italic !important;
    font-weight: 500 !important;
    font-family: ${cardisTheme.fonts.body} !important;
    ${printStyles}
  }
`

export const PropertyAddress = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.5;

  @media print {
    font-size: 13px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    line-height: 1.5 !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 400 !important;
    ${printStyles}
  }
`

export const ClientSection = styled.div`
  margin-top: 64px;
`

export const SectionLabel = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 6px 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media print {
    font-size: 12px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 0 6px 0 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    font-family: ${cardisTheme.fonts.body} !important;
    ${printStyles}
  }
`

export const ClientName = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};

  @media print {
    font-size: 13px !important;
    color: ${cardisTheme.colors.primary} !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 600 !important;
    margin: 0 !important;
    ${printStyles}
  }
`

export const ClientContact = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.6;

  @media print {
    font-size: 12px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    line-height: 1.6 !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 400 !important;
    ${printStyles}
  }
`

export const BrokerSection = styled.div`
  display: flex;
  flex-direction: column;

  @media print {
    display: flex !important;
    flex-direction: column !important;
  }
`

export const BrokerPhoto = styled.img`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${cardisTheme.colors.white};
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  margin-bottom: 13px;

  @media print {
    width: 160px !important;
    height: 160px !important;
    overflow: hidden !important;
    min-height: auto !important;
    border-radius: 50% !important;
    margin-bottom: 13px !important;
    ${printStyles}
  }
`

export const BrokerPhotoPlaceholder = styled.div`
  width: 112px;
  height: 112px;
  border-radius: 50%;
  background: ${cardisTheme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid ${cardisTheme.colors.white};
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  margin-bottom: 13px;
  color: ${cardisTheme.colors.white};
  font-size: 38px;
  font-weight: 600;

  @media print {
    width: 112px !important;
    height: 112px !important;
    border-radius: 50% !important;
    background: ${cardisTheme.colors.primary} !important;
    ${printStyles}
  }
`

export const BrokerLabel = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-style: italic;
  color: ${cardisTheme.colors.primary};
  margin: 10px 0 6px 0;

  @media print {
    font-size: 12px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 0 6px 0 !important;
    font-style: italic !important;
    font-family: ${cardisTheme.fonts.body} !important;
    ${printStyles}
  }
`

export const BrokerName = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 18px;
  font-weight: 700;
  color: ${cardisTheme.colors.primary};

  @media print {
    font-size: 18px !important;
    color: ${cardisTheme.colors.primary} !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 700 !important;
    margin: 0 !important;
    ${printStyles}
  }
`

export const BrokerContact = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.6;

  @media print {
    font-size: 12px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    line-height: 1.6 !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 400 !important;
    ${printStyles}
  }
`

export const BrokerPhone = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  letter-spacing: 1px;

  @media print {
    font-size: 14px !important;
    color: ${cardisTheme.colors.primary} !important;
    letter-spacing: 1px !important;
    font-family: ${cardisTheme.fonts.body} !important;
    font-weight: 600 !important;
    margin: 0 !important;
    ${printStyles}
  }
`

// Duo broker layout
export const BrokerDuoContainer = styled.div`
  display: flex;
  gap: 10px;

  @media print {
    display: flex !important;
    gap: 10px !important;
  }
`

export const BrokerDuoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
  }
`

export const BrokerPhotoDuo = styled.img`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${cardisTheme.colors.white};
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  margin-bottom: 8px;

  @media print {
    width: 110px !important;
    height: 110px !important;
    overflow: hidden !important;
    min-height: auto !important;
    border-radius: 50% !important;
    margin-bottom: 8px !important;
    ${printStyles}
  }
`

export const BrokerPhotoPlaceholderDuo = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${cardisTheme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid ${cardisTheme.colors.white};
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  margin-bottom: 8px;
  color: ${cardisTheme.colors.white};
  font-size: 26px;
  font-weight: 600;

  @media print {
    width: 80px !important;
    height: 80px !important;
    border-radius: 50% !important;
    background: ${cardisTheme.colors.primary} !important;
    ${printStyles}
  }
`

export const BrokerNameDuo = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 700;
  color: ${cardisTheme.colors.primary};
  text-align: center;
  margin: 0;

  @media print {
    font-size: 13px !important;
    color: ${cardisTheme.colors.primary} !important;
    font-weight: 700 !important;
    margin: 0 !important;
    ${printStyles}
  }
`

export const BrokerContactDuo = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  text-align: center;

  @media print {
    font-size: 10px !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 !important;
    ${printStyles}
  }
`

export const BrokerPhoneDuo = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 11px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  letter-spacing: 0.5px;
  text-align: center;
  margin: 2px 0 0 0;

  @media print {
    font-size: 11px !important;
    color: ${cardisTheme.colors.primary} !important;
    letter-spacing: 0.5px !important;
    font-weight: 600 !important;
    margin: 2px 0 0 0 !important;
    ${printStyles}
  }
`

export const AddBrokerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  margin-top: 8px;
  border: 1px dashed ${cardisTheme.colors.primary};
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  color: ${cardisTheme.colors.primary};
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  @media print {
    display: none !important;
  }
`

export const RemoveBrokerButton = styled.button`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: ${cardisTheme.colors.primary};
  color: white;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 0;

  &:hover {
    background: ${cardisTheme.colors.danger};
  }

  @media print {
    display: none !important;
  }
`

export const BrokerDuoColumnWrapper = styled.div`
  position: relative;

  &:hover ${RemoveBrokerButton} {
    opacity: 0.6;
  }
`

export const FooterWrapper = styled.div`
  position: relative;
  margin-top: auto;

  @media print {
    position: relative !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
  }
`

export const FooterPattern = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 160px;
  z-index: 0;
  object-fit: cover;
  background-color: white;

  @media print {
    position: absolute !important;
    bottom: 0 !important;
    left: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    height: 160px !important;
    object-fit: cover !important;
    overflow: hidden !important;
    border: none !important;
    ${printStyles}
  }
`

export const Footer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 48px 51px;

  @media print {
    position: relative !important;
    z-index: 1 !important;
    margin: 0 !important;
    padding: 48px 51px !important;
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-end !important;
    border: none !important;
  }
`

export const OfficeInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

export const LocationPinIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;

  @media print {
    width: 20px !important;
    height: 20px !important;
    min-height: auto !important;
    object-fit: contain !important;
    ${printStyles}
  }
`

export const OfficeDetails = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`

export const OfficeAddress = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.5;
  white-space: pre-line;
`

export const OfficePhone = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 3px 0 0 0;
`

export const CompanyContact = styled.div`
  text-align: right;
`

export const CompanyEmail = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 11px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0;
`

export const CompanyWebsite = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 3px 0 0 0;
`
